import { OverlayType } from '../constants/common'
import utils from '../utils'
import { drawInVisibleArea, getFirstBarToRenderIndex } from '../utils/draw_in_visible_area'
import Overlay from './overlay'

interface Attrs {
  period: number
  channelStrokeColor: string
  channelFillColor: string
  medianBandColor: string
}

const DEFAULT_PERIOD = 20

const COLORS = {
  ChannelStroke: '#69c1ea',
  ChannelFill: '#69C1EA12',
  MedianBandStroke: '#ffa75f',
}

class DonchianChannels extends Overlay<Attrs> {
  static type = OverlayType.Dch
  static label = 'Donchian Channels'

  set(obj: Partial<Attrs>) {
    super.set(obj)
    const { period } = obj
    if (typeof period === 'string') {
      this.attrs.period = parseInt(period, 10)
      this.trigger('change')
    }
    return this
  }

  renderContent(context: CanvasRenderingContext2D) {
    super.renderContent()
    const { leftOffset, width } = this.model.chart()
    const { period } = this.attrs
    const upperBand: number[] = []
    const lowerBand: number[] = []
    const medianBand: number[] = []
    const firstBarToRender = getFirstBarToRenderIndex(this.data.close, period - 1, this.fx, leftOffset)
    let lastBarToRender = 0
    if (firstBarToRender === undefined) {
      return
    }

    context.save()
    context.translate(0.5, 0.5)
    context.set('lineWidth', 1)
    context.set('strokeStyle', COLORS.ChannelStroke)
    context.set('fillStyle', COLORS.ChannelFill)

    //upper band + clip
    context.beginPath()
    drawInVisibleArea(this.data.close, firstBarToRender, this.fx, leftOffset, width, (i, x) => {
      upperBand[i] = utils.max(this.data.high.slice(Math.max(0, i - period), i + 1))
      lowerBand[i] = utils.min(this.data.low.slice(Math.max(0, i - period), i + 1))
      medianBand[i] = (upperBand[i] + lowerBand[i]) / 2

      context.lineTo(x, Math.round(this.fy(upperBand[i])))
      lastBarToRender = i
    })
    context.stroke()
    context.lineTo(this.fx(lastBarToRender), context.canvas.height)
    context.lineTo(this.fx(firstBarToRender), context.canvas.height)
    context.clip()

    // lower band + fill
    context.beginPath()
    for (let i = firstBarToRender; i <= lastBarToRender; i++) {
      const x = this.fx(i)
      context.lineTo(x, Math.round(this.fy(lowerBand[i])))
    }
    context.stroke()
    context.lineTo(this.fx(lastBarToRender), 0)
    context.lineTo(0, 0)
    context.fill()

    // median band
    context.set('strokeStyle', COLORS.MedianBandStroke)
    context.beginPath()
    for (let i = firstBarToRender; i <= lastBarToRender; i++) {
      const x = this.fx(i)
      context.lineTo(x, Math.round(this.fy(medianBand[i])))
    }
    context.stroke()
    context.restore()
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.attrs.period ?? DEFAULT_PERIOD,
      required: true,
      min: 1,
      max: 999999,
    }

    const strokeColor = {
      type: 'color',
      label: 'Channels Stroke Color',
      name: 'strokeColor',
      value: this.attrs.channelStrokeColor ?? this.getFreeColor(),
    }
    const fillColor = {
      type: 'color',
      label: 'Channels Fill Color',
      name: 'fillColor',
      value: this.attrs.channelFillColor ?? this.getFreeColor(),
    }
    const medianBandColor = {
      type: 'color',
      label: 'Median Band Color',
      name: 'medianBandColor',
      value: this.attrs.medianBandColor ?? this.getFreeColor(),
    }

    return {
      title: this.label,
      inputs: [period, medianBandColor, strokeColor, fillColor],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
      },
    }
  }

  getIsValid(key: string) {
    switch (key) {
      case 'period':
        return this.getIsNumberInputValid({ key })
      case 'medianBandColor':
      case 'strokeColor':
      case 'fillColor':
        return true
      default:
        return false
    }
  }

  getLabelColor() {
    return this.attrs.medianBandColor
  }

  toString() {
    return `Donchian ${this.attrs.period}`
  }
}

DonchianChannels.prototype.defaults = {
  medianBandColor: COLORS.MedianBandStroke,
  channelStrokeColor: COLORS.ChannelStroke,
  channelFillColor: COLORS.ChannelFill,
}

export default DonchianChannels
